import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@ophelos/ui/components/drawer';
import { Check, ChevronRight, CircleAlert, CreditCard, Loader2 } from 'lucide-react';
import { Trans } from 'react-i18next';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from '@/hooks/useTranslation';
import { formatMoneyInLanguage, fromSubunits } from '@/lib/currency';
import { buildPlanSchedule, type PlanFrequency } from '@/lib/payment-schedule';

/**
 * The card is saved as a payment intent before any instalment is taken, so the bank shows a
 * zero-amount authorisation rather than the first instalment. Not a placeholder.
 */
const AUTHORISATION_AMOUNT = 0;

export interface PaymentPlanReviewData {
  startDate: Date;
  frequency: PlanFrequency;
  paymentCount: number;
}

interface PlanAuthoriseSheetProps {
  open: boolean;
  /** Dismissed without authorising; the plan tab underneath keeps the configured plan. */
  onClose: () => void;
  /** Discounted plan total the schedule is built from. */
  totalAmount: number;
  currency?: string;
  /** Creditor/client display name for the "informed of your payment" line. */
  clientName: string;
  plan: PaymentPlanReviewData;
  onAuthorise: () => void;
  isProcessing: boolean;
  error: string | null;
}

/**
 * The card plan's last word before the card sheet: what the authorisation looks like on the
 * customer's statement and when the creditor hears about it. Opens over the plan tab, so the
 * plan itself is not restated here.
 */
export function PlanAuthoriseSheet({ open, onClose, totalAmount, currency = 'EUR', clientName, plan, onAuthorise, isProcessing, error }: PlanAuthoriseSheetProps) {
  const { t, currentLanguage } = useTranslation();
  const isMobile = useIsMobile();

  const instalments = buildPlanSchedule(totalAmount, plan.paymentCount, plan.startDate, plan.frequency);
  const firstAmount = fromSubunits(instalments[0].subunits);
  const now = new Date();
  const isToday =
    plan.startDate.getDate() === now.getDate() && plan.startDate.getMonth() === now.getMonth() && plan.startDate.getFullYear() === now.getFullYear();
  const dateFmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const startLabel = plan.startDate.toLocaleDateString(currentLanguage, dateFmt);
  const summaryParams = {
    count: plan.paymentCount,
    amount: formatMoneyInLanguage(firstAmount, currentLanguage, currency),
    frequency: t(`paymentPlan.${plan.frequency}`),
    date: startLabel,
  };

  const body = (
    <div className="stack gap-6">
      <p className="text-sm text-muted-foreground">
        {isToday ? t('paymentPlan.review.summaryToday', summaryParams) : t('paymentPlan.review.summaryFrom', summaryParams)}
      </p>

      <div className="stack gap-4">
        <div className="flex gap-3 items-start">
          <div className="highlight-accent rounded-full p-2 shrink-0">
            <CreditCard className="size-4" />
          </div>
          <div>
            <p className="text-sm font-medium">{t('paymentPlan.review.authTitle')}</p>
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="paymentPlan.review.authSub"
                components={{ amount: <CurrencyDisplay amount={AUTHORISATION_AMOUNT} currency={currency} className="inline-flex gap-0" /> }}
              />
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-start">
          <div className="highlight-accent rounded-full p-2 shrink-0">
            <Check className="size-4" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {isToday
                ? t('paymentPlan.review.clientInformedTitle', { client: clientName })
                : t('paymentPlan.review.clientInformedTitleDeferred', { client: clientName, date: startLabel })}
            </p>
            <p className="text-sm text-muted-foreground">
              {isToday ? t('paymentPlan.review.clientInformedSub', { client: clientName }) : t('paymentPlan.review.clientInformedSubDeferred', { client: clientName })}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <CircleAlert />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button onClick={onAuthorise} size="xl" className="group w-full" disabled={isProcessing}>
        {isProcessing ? (
          <>
            <Loader2 className="size-5 mr-2 animate-spin" />
            {t('verify.processing')}
          </>
        ) : (
          <>
            {/* Wrap the label so the button's flex gap sits before the chevron only —
                not between "pay" and the amount that <Trans> would otherwise spill. */}
            <span>
              {isToday ? (
                <Trans
                  i18nKey="paymentPlan.review.ctaToday"
                  components={{ amount: <CurrencyDisplay amount={firstAmount} currency={currency} className="inline-flex gap-0" /> }}
                />
              ) : (
                t('paymentPlan.review.ctaDeferred')
              )}
            </span>
            <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </>
        )}
      </Button>
      <Button variant="ghost" onClick={onClose} disabled={isProcessing} className="w-full">
        {t('paymentPlan.review.editPlan')}
      </Button>
    </div>
  );

  const onOpenChange = (next: boolean) => {
    if (!next && !isProcessing) onClose();
  };

  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-card">
        <DrawerHeader className="text-left">
          <DrawerTitle>{t('paymentPlan.review.title')}</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-6">{body}</div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl px-6">
        <DialogHeader className="py-2">
          <DialogTitle>{t('paymentPlan.review.title')}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
